import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Share2, Lock, Upload, Save, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const API_BASE = API_URL.replace('/api', '');

const TITLE_MAX = 90;
const DESCRIPTION_MAX = 200;

// Aperçu de lien (Snapchat, WhatsApp, LinkedIn, iMessage...) — plans Premium
// et Signature. Ce que voit une personne qui reçoit un lien vers le site :
// titre, texte et image, rendus côté serveur (backend/src/utils/htmlShell.js).
// Vides, ils reprennent le nom, le slogan et le logo de l'entreprise.
export default function SharePreviewSection() {
  const { user, company, refreshMe } = useAuth();
  const [allowed, setAllowed] = useState(null); // null : inconnu, false : plan insuffisant
  const [domain, setDomain] = useState(null);
  const [form, setForm] = useState({ shareTitle: '', shareDescription: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/billing/status');
      setAllowed(['PREMIUM', 'SIGNATURE'].includes(data.effectivePlan));
      setDomain(data.domain);
    } catch {
      setAllowed(null);
      setDomain(null);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'MEMBER') load();
  }, [user, load]);

  useEffect(() => {
    if (company) {
      setForm({ shareTitle: company.shareTitle || '', shareDescription: company.shareDescription || '' });
    }
  }, [company]);

  if (user?.role === 'MEMBER' || allowed === null) return null;

  if (!allowed) {
    return (
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Share2 size={17} /> Aperçu de lien
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--text-light)', fontWeight: 400 }}>
            <Lock size={12} /> Premium
          </span>
        </h3>
        <p style={{ fontSize: '0.88rem', color: 'var(--text-light)', lineHeight: 1.6 }}>
          Quand vous partagez un lien vers votre site sur Snapchat, WhatsApp, LinkedIn ou
          par message, l'aperçu affiche <strong>votre nom, votre texte et votre logo</strong>
          {' '}au lieu de Jalonne — disponible à partir du plan <strong>Premium</strong>.
        </p>
      </div>
    );
  }

  const defaultTitle = `${company?.name || ''} — Suivi de chantier`;
  const defaultDescription = company?.tagline || company?.description
    || `Suivez l'avancement des chantiers de ${company?.name || ''}.`;
  const previewTitle = form.shareTitle.trim() || defaultTitle;
  const previewDescription = form.shareDescription.trim() || defaultDescription;
  const previewImage = company?.shareImage || company?.logo;
  const previewHost = domain || window.location.host;

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/users/company', form);
      await refreshMe();
      toast.success('Aperçu de lien enregistré');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('shareImage', file);
    setBusy(true);
    try {
      await api.post('/users/company/share-image', fd);
      await refreshMe();
      toast.success('Image de l\'aperçu mise à jour');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const removeImage = async () => {
    setBusy(true);
    try {
      await api.put('/users/company', { shareImage: null });
      await refreshMe();
      toast.success('Image retirée — le logo sera utilisé');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Share2 size={17} /> Aperçu de lien
      </h3>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: 16, lineHeight: 1.6 }}>
        Ce que voit une personne qui reçoit un lien vers votre site (Snapchat, WhatsApp,
        LinkedIn, iMessage...). Laissez vide pour utiliser votre nom, votre slogan et votre
        logo. Sur une page chantier, le nom du chantier et sa première photo sont utilisés.
      </p>

      {/* Prévisualisation, à la manière d'une messagerie */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'stretch', border: '1px solid var(--border)',
        borderRadius: 10, overflow: 'hidden', background: '#f8fafc', marginBottom: 18,
      }}>
        <div style={{ width: 92, minHeight: 92, flexShrink: 0, background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {previewImage ? (
            <img src={`${API_BASE}/uploads/${previewImage}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Share2 size={22} color="#94a3b8" />
          )}
        </div>
        <div style={{ padding: '10px 12px 10px 0', minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {previewTitle}
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-light)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {previewDescription}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 6 }}>{previewHost}</div>
        </div>
      </div>

      <div className="form-group">
        <label>Titre de l'aperçu</label>
        <input value={form.shareTitle} maxLength={TITLE_MAX} placeholder={defaultTitle}
          onChange={e => setForm({ ...form, shareTitle: e.target.value })} />
        <small style={{ color: 'var(--text-light)' }}>{form.shareTitle.length}/{TITLE_MAX}</small>
      </div>
      <div className="form-group">
        <label>Texte de l'aperçu</label>
        <textarea rows={2} value={form.shareDescription} maxLength={DESCRIPTION_MAX} placeholder={defaultDescription}
          onChange={e => setForm({ ...form, shareDescription: e.target.value })} />
        <small style={{ color: 'var(--text-light)' }}>{form.shareDescription.length}/{DESCRIPTION_MAX}</small>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <label className="btn btn-sm" style={{ cursor: 'pointer' }}>
          <Upload size={14} /> {company?.shareImage ? 'Changer l\'image' : 'Choisir une image'}
          <input type="file" accept="image/*" onChange={uploadImage} style={{ display: 'none' }} disabled={busy} />
        </label>
        {company?.shareImage && (
          <button className="btn btn-sm" onClick={removeImage} disabled={busy}>
            <Trash2 size={13} /> Retirer (utiliser le logo)
          </button>
        )}
        <span style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
          {company?.shareImage ? 'Image dédiée.' : 'Sans image dédiée, votre logo est utilisé.'} Format conseillé : 1200 × 630.
        </span>
      </div>

      <button className="btn btn-primary" onClick={save} disabled={busy}>
        <Save size={16} /> {busy ? 'Enregistrement...' : 'Enregistrer'}
      </button>
    </div>
  );
}
